/**
 * Cart Duplicate Quantity
 *
 * Replaces the standard cart quantity selector with a custom one that:
 * - PLUS: adds a fresh copy of the item as a new cart line (same variant + properties)
 * - MINUS: decreases quantity by 1 on the existing line (removes if at 1)
 *
 * Works on both the cart page and the cart drawer.
 */

(function () {
  // Inject styles
  const style = document.createElement('style');
  style.textContent = `
    .dup-qty-controls {
      display: inline-flex;
      align-items: center;
      gap: 0;
      border: 1px solid #00000033;
      overflow: hidden;
      height: 30px;
      background: #fff;
    }
    .dup-qty-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 30px;
      height: 100%;
      background: transparent;
      border: none;
      cursor: pointer;
      padding: 0;
      color: currentColor;
      transition: background 0.15s;
    }
    .dup-qty-btn:hover:not(:disabled) {
      background: rgb(0 0 0 / 0.06);
    }
    .dup-qty-btn:disabled {
      opacity: 0.35;
      cursor: not-allowed;
    }
    .dup-qty-btn .svg-wrapper {
      display: flex;
      align-items: center;
      justify-content: center;
      pointer-events: none;
    }
    .dup-qty-btn svg {
      width: 12px;
      height: 12px;
    }
    .dup-qty-count {
      min-width: 36px;
      text-align: center;
      font-size: 12px;
      font-weight: 500;
      padding: 0 4px;
      user-select: none;
      line-height: 36px;
    }
    .dup-qty-loading-overlay {
      position: fixed;
      inset: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: rgba(255, 255, 255, 0.7);
      z-index: 9999;
      opacity: 0;
      pointer-events: none;
      transition: opacity 0.2s ease;
    }
    dialog .dup-qty-loading-overlay {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
    }
    .dup-qty-loading-overlay.active {
      opacity: 1;
      pointer-events: all;
    }
    .dup-qty-loading-spinner {
      width: 40px;
      height: 40px;
      border: 3px solid rgba(0, 0, 0, 0.1);
      border-top-color: rgba(0, 0, 0, 0.5);
      border-radius: 50%;
      animation: dup-qty-spin 0.7s linear infinite;
    }
    @keyframes dup-qty-spin {
      to { transform: rotate(360deg); }
    }
  `;
  document.head.appendChild(style);

  // Create the loading overlay element once
  const loadingOverlay = document.createElement('div');
  loadingOverlay.className = 'dup-qty-loading-overlay';
  loadingOverlay.innerHTML = '<div class="dup-qty-loading-spinner"></div>';
  document.body.appendChild(loadingOverlay);

  // Create a second overlay for inside the cart drawer dialog (top layer)
  const drawerLoadingOverlay = document.createElement('div');
  drawerLoadingOverlay.className = 'dup-qty-loading-overlay';
  drawerLoadingOverlay.innerHTML = '<div class="dup-qty-loading-spinner"></div>';

  function getDrawerDialog() {
    return document.querySelector('cart-drawer-component dialog[open]');
  }

  function showLoading() {
    const dialog = getDrawerDialog();
    if (dialog) {
      // Append overlay inside the open dialog so it renders in the top layer
      if (!drawerLoadingOverlay.parentNode || drawerLoadingOverlay.parentNode !== dialog) {
        dialog.appendChild(drawerLoadingOverlay);
      }
      drawerLoadingOverlay.classList.add('active');
    } else {
      loadingOverlay.classList.add('active');
    }
  }

  function hideLoading() {
    loadingOverlay.classList.remove('active');
    drawerLoadingOverlay.classList.remove('active');
  }

  // ---- Operation lock to prevent concurrent cart API calls ----
  let operationInProgress = false;

  /**
   * Refresh the cart UI after a change.
   * Tries to reuse the addon manager's refreshCartUI if available,
   * otherwise falls back to a simple section re-fetch + cart:update dispatch.
   */
  async function refreshCart() {
    // Use the addon manager's refreshCartUI so the cart:update 'cart-add-on-new'
    // event fires, which hides our loading overlays correctly.
    if (window._cartAddonManager) {
      await window._cartAddonManager.refreshCartUI();
      return;
    }

    // Fallback: refresh the cart-items-component section manually
    const cartComponent = document.querySelector('cart-items-component');
    if (!cartComponent || !cartComponent.dataset.sectionId) {
      window.location.reload();
      return;
    }
    const sectionId = cartComponent.dataset.sectionId;
    const [cartData, responseText] = await Promise.all([
      fetch('/cart.js').then((r) => r.json()),
      fetch('/cart?section_id=' + sectionId).then((r) => r.text()),
    ]);
    const html = new DOMParser().parseFromString(responseText, 'text/html');
    const newContent = html.querySelector('cart-items-component');
    if (newContent) cartComponent.innerHTML = newContent.innerHTML;

    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: { data: { itemCount: cartData.item_count, source: 'cart-add-on-new' } },
      })
    );
  }

  /**
   * Handles the PLUS button — re-adds the item as a new line, duplicating
   * all upsell and cross-sell items that belong to the same product group.
   * @param {HTMLButtonElement} btn
   */
  window.dupQtyIncrease = async function (btn) {
    if (operationInProgress) return;
    operationInProgress = true;
    btn.disabled = true;
    showLoading();

    const newLine = btn.dataset.newLine === 'true';

    if (!newLine) {
      // Product has NO upsell/cross-sell metafields
      const line = parseInt(btn.dataset.line ?? '0', 10);
      const currentQty = parseInt(btn.dataset.quantity ?? '0', 10);
      try {
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ line, quantity: currentQty + 1 }),
        });
        if (!res.ok) throw new Error('Failed to update quantity');
        await refreshCart();
      } catch (err) {
        console.error('dupQtyIncrease error:', err);
        btn.disabled = false;
      } finally {
        operationInProgress = false;
        hideLoading();
      }
      return;
    }

    // Product HAS upsell/cross-sell metafields
    const mainVariantId = btn.dataset.variantId;
    const propertiesRaw = btn.dataset.properties;

    let originalProperties = {};
    try {
      originalProperties = JSON.parse(propertiesRaw || '{}');
    } catch {
      // no-op
    }

    try {
      // Generate a new unique group ID for the duplicate item
      const newGroupId = Date.now().toString(36) + Math.random().toString(36).slice(2);

      // Build the new main item properties — same as original but with a fresh group ID
      // to create a separate cart line. Strip the old _instance if present.
      const mainProps = Object.assign({}, originalProperties, {
        _product_group_id: newGroupId,
        _is_upsell: 'false',
      });
      delete mainProps._instance;

      // Add only the main product as a new line — do NOT duplicate upsell/cross-sell items
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{ id: mainVariantId, quantity: 1, properties: mainProps }],
        }),
      });
      if (!res.ok) throw new Error('Failed to add items');

      await refreshCart();
    } catch (err) {
      console.error('dupQtyIncrease error:', err);
      btn.disabled = false;
    } finally {
      operationInProgress = false;
      hideLoading();
    }
  };

  /**
   * Handles the MINUS button — decreases quantity by 1.
   * When quantity reaches 0 (removing the item), also removes all
   * associated upsell/cross-sell items in the same product group.
   * @param {HTMLButtonElement} btn
   */
  window.dupQtyDecrease = async function (btn) {
    if (operationInProgress) return;
    operationInProgress = true;
    btn.disabled = true;
    showLoading();

    const line = parseInt(btn.dataset.line ?? '0', 10);
    const currentQty = parseInt(btn.dataset.quantity ?? '0', 10);
    const newQty = currentQty - 1;
    const groupId = btn.dataset.groupId || '';
    const variantId = btn.dataset.variantId || '';

    try {
      if (newQty > 0) {
        // Simple decrease — no addons to worry about
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ line, quantity: newQty }),
        });
        if (!res.ok) throw new Error('Failed to update quantity');
      } else {
        // Removing the item entirely — also remove associated upsells/cross-sells.
        // Fetch live cart to find addon lines for this group.
        const cartRes = await fetch('/cart.js');
        const cart = await cartRes.json();
        const items = cart.items;

        // Collect 1-based line numbers to remove: the main item + its addons
        const linesToRemove = [];
        for (let i = 0; i < items.length; i++) {
          const item = items[i];
          const props = item.properties || {};
          const itemGroupId = String(props._product_group_id || '').trim();

          // The main item itself
          if (i + 1 === line) {
            linesToRemove.push(i + 1);
            continue;
          }

          // Addon lines belonging to same group + pointing at same variant
          if (
            groupId &&
            itemGroupId === groupId.trim() &&
            props._upsell_for &&
            String(props._upsell_for).trim() === variantId.trim()
          ) {
            linesToRemove.push(i + 1);
          }
        }

        // Remove in reverse order so earlier line numbers stay valid
        linesToRemove.sort((a, b) => b - a);
        for (const ln of linesToRemove) {
          const res = await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ line: ln, quantity: 0 }),
          });
          if (!res.ok) throw new Error('Failed to remove line ' + ln);
        }
      }

      await refreshCart();
    } catch (err) {
      console.error('dupQtyDecrease error:', err);
      btn.disabled = false;
    } finally {
      operationInProgress = false;
      hideLoading();
    }
  };
})();
